"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import FormInput from "../../../components/FormInput";
import Dropdown from "../../../components/Dropdown";
import Button from "../../../components/Button";
import FileInput from "../../../components/FileInput";
import ErrorModal from "../../../components/ErrorModal";

import styles from "../css/dashboard.module.css";
import formStyles from "../css/addbranch.module.css";
import { addBranch, uploadBranchDoc, getCompany } from "../../../api/branchManagement]/branchApi";

export default function AddBranch() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    branch_name: "",
    branch_code: "",
    pharmacy_license: "",
    address: "",
    suburb: "",
    province: "",
    postal_code: "",
    country: "South Africa",
    telephone: "",
    email: "",
    cert_doc: null,
    dispensing_fee: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  const handleChange = (name) => (e) => {
    if (name === "cert_doc") {
      setFormData({ ...formData, [name]: e.target.files[0] });
    } else {
      let value = e.target.value;
      if (name === "telephone") {
        // Allow only digits, max 10
        value = value.replace(/\D/g, "").slice(0, 10);
      }
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.branch_code) {
      setErrorMessage("Please enter a Branch Code before uploading the certification document.");
      setShowError(true);
      return;
    }
    try {
      setSubmitting(true);
      let docurl = null;
      if (formData.cert_doc) {
        docurl = await uploadBranchDoc(formData.branch_code, formData.cert_doc);
      }
      // Fetch the company linked to the logged-in director and get cipcregistrationnumber
      let cipc = null;
      try {
        const res = await getCompany();
        cipc = res?.cipcregistrationnumber || null;
      } catch (_) {}
      await addBranch({
        branchname: formData.branch_name,
        branchcode: formData.branch_code,
        pharmacylicenceno: formData.pharmacy_license,
        address: formData.address,
        suburb: formData.suburb,
        province: formData.province,
        postalcode: formData.postal_code,
        country: formData.country,
        telephoneno: formData.telephone,
        email: formData.email,
        docurl,
        dispensingfee: formData.dispensing_fee === "" ? null : Number(formData.dispensing_fee),
        cipcregistrationnumber: cipc,
      });
      navigate(-1);
    } catch (err) {
      const rawMessage =
        (err && err.response && err.response.data && err.response.data.message) ||
        err?.message ||
        "Something went wrong while adding branch";

      const msg = String(rawMessage || "");

      // Detect Prisma unique constraint error on branchcode and email and show friendly messages
      if (
        msg.includes("Unique constraint failed") &&
        msg.includes("branchcode")
      ) {
        setErrorMessage("A branch with this Branch Code already exists. Please use a different Branch Code.");
      } else if (
        msg.includes("Unique constraint failed") &&
        msg.toLowerCase().includes("email")
      ) {
        setErrorMessage("This email address is already linked to another branch. Please use a different email address.");
      } else {
        setErrorMessage(rawMessage);
      }
      setShowError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.app}>
      <div className={styles.mainContainer}>
        <Navbar />

        <main className={styles.content}>
          <h1 className={formStyles.pageTitle}>Add Branch</h1>

          <form className={formStyles.formContainer} onSubmit={handleSubmit}>
            {/* --- First Section: Branch Info --- */}
            <div className={formStyles.formRowFourFields}>
              <FormInput
                label="Branch Name"
                name="branch_name"
                value={formData.branch_name}
                onChange={handleChange("branch_name")}
              />
              <FormInput
                label="Branch Code"
                name="branch_code"
                value={formData.branch_code}
                onChange={handleChange("branch_code")}
              />
              <FormInput
                label="Pharmacy Practice Number"
                name="pharmacy_license"
                value={formData.pharmacy_license}
                onChange={handleChange("pharmacy_license")}
              />
            </div>
            <hr className={formStyles.sectionDivider} />

            {/* --- Second Section: Address Info --- */}
            <div className={formStyles.formRowFourFields}>
              <FormInput
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange("address")}
              />
              <FormInput
                label="Suburb"
                name="suburb"
                value={formData.suburb}
                onChange={handleChange("suburb")}
              />
              <Dropdown
                label="Province"
                name="province"
                options={[
                  "Eastern Cape",
                  "Free State",
                  "Gauteng",
                  "KwaZulu-Natal",
                  "Limpopo",
                  "Mpumalanga",
                  "Northern Cape",
                  "North West",
                  "Western Cape",
                ]}
                value={formData.province}
                onChange={(value) => setFormData({ ...formData, province: value })}
              />
              <FormInput
                label="Postal Code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange("postal_code")}
              />
            </div>
            <hr className={formStyles.sectionDivider} />

            {/* --- Third Section: Contact Info --- */}
            <div className={formStyles.formRowFourFields}>
              <FormInput
                label="Country"
                name="country"
                value={formData.country}
                disabled
              />
              <FormInput
                label="Telephone Number"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange("telephone")}
                placeholder="Enter 10-digit number"
              />
              <FormInput
                label="Email Address"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange("email")}
              />
              <FormInput
                label="Dispensing Fee"
                type="number"
                name="dispensing_fee"
                value={formData.dispensing_fee}
                onChange={(e) => {
                  const v = e.target.value;
                  const num = Number(v);
                  if (!Number.isFinite(num)) {
                    setFormData({ ...formData, dispensing_fee: "" });
                  } else {
                    setFormData({ ...formData, dispensing_fee: num < 0 ? "0" : v });
                  }
                }}
                placeholder="0.00"
                prefix="R"
                min={0}
                step={1}
              />
            </div>
            <hr className={formStyles.sectionDivider} />
            <div className={formStyles.formRow}>
              <FileInput
                label="Certification Document"
                name="cert_doc"
                id="cert_doc"
                value={formData.cert_doc}
                onChange={handleChange("cert_doc")}
                accept=".pdf,.doc,.docx"
                wide={true}
              />
            </div>
            <div className={formStyles.formActions}>
              <Button variant="primary" type="submit" disabled={submitting}>Save</Button>
              <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
            </div>
          </form>
        </main>
        <div className={styles.footerWrapper}>
          <Footer />
        </div>
      </div>
      <ErrorModal
        isOpen={showError}
        onClose={() => setShowError(false)}
        message={errorMessage}
      />
    </div>
  );
}